
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText } from "lucide-react";
import { Role, Candidate } from "./candidate-types";

interface RoleTableProps {
  roles: Role[];
  candidatesPerRole: {
    [roleId: string]: Candidate[];
  };
  selectedRowId: string | null;
  onRowSelect: (rowId: string) => void;
  loading: boolean;
  currentProcessingId: string | null;
  searching: boolean;
  rolesLoading: boolean;
  onViewJD: (roleId: string) => void;
}

export const RoleTable = ({
  roles,
  candidatesPerRole,
  selectedRowId,
  onRowSelect,
  loading,
  currentProcessingId,
  searching,
  rolesLoading,
  onViewJD
}: RoleTableProps) => {
  const formatCandidateCell = (candidate: Candidate | null, isSelected: boolean) => {
    if (!candidate) return null;
    
    return (
      <div className="flex items-center gap-2">
        <span className={cn("text-[11px]", isSelected ? 'text-black' : 'text-white')}>
          {candidate.candidate_name}
        </span>
      </div>
    );
  };

  const hasValidJobDescription = (role: Role) => {
    const hasPdf = role.role_description?.pdf_url;
    const hasValidText = role.role_description?.text && 
                        role.role_description.text.trim() !== '' && 
                        role.role_description.text !== 'No JD found';
    
    return hasPdf || hasValidText;
  };

  const getTopCandidates = (roleId: string) => {
    const roleCandidates = candidatesPerRole[roleId] || [];
    // Sort by match percentage descending, then by id for consistency
    const sortedCandidates = roleCandidates.sort((a, b) => {
      if (b.matchPercentage !== a.matchPercentage) {
        return b.matchPercentage - a.matchPercentage;
      }
      return a.id - b.id;
    });
    return sortedCandidates.slice(0, 3);
  };

  const renderTableContent = () => {
    if (rolesLoading) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="text-[12px] text-left text-white py-2">
            Loading roles...
          </TableCell>
        </TableRow>
      );
    }

    if (!roles.length) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="text-[12px] text-left text-white py-2">
            No roles with JDs found. Please ensure roles have JDs in Step 3.
          </TableCell>
        </TableRow>
      );
    }

    return roles.map((role, index) => {
      const topCandidates = getTopCandidates(role.id);
      const isSelected = selectedRowId === role.id;
      const isTableDisabled = searching && currentProcessingId !== null;

      return (
        <TableRow
          key={role.id}
          className={cn(
            "transition-colors",
            isTableDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
            "hover:bg-primary/20",
            isSelected ? 'bg-white' : 'bg-secondary-light/5'
          )}
          onClick={() => !loading && !isTableDisabled && onRowSelect(role.id)}
        >
          <TableCell className={cn("text-[11px] text-left p-1", isSelected ? 'text-black' : 'text-primary-light')}>
            {index + 1}
          </TableCell>
          <TableCell className={cn("text-[11px] text-left p-1", isSelected ? 'text-black' : 'text-primary-light')}>
            {role.role_title}
          </TableCell>
          <TableCell className="text-left p-1">
            {hasValidJobDescription(role) ? (
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  onViewJD(role.id);
                }}
                className={`flex items-center gap-1 text-[11px] hover:underline ${isSelected ? 'text-black' : 'text-primary'}`}
              >
                <FileText size={12} />
                View JD
              </button>
            ) : (
              <span className="text-[11px] text-primary-light/50">No JD</span>
            )}
          </TableCell>
          <TableCell className={cn("text-[11px] text-left p-1", isSelected ? 'text-black' : 'text-primary-light')}>
            {formatCandidateCell(topCandidates[0], isSelected)}
          </TableCell>
          <TableCell className={cn("text-[11px] text-left p-1", isSelected ? 'text-black' : 'text-primary-light')}>
            {formatCandidateCell(topCandidates[1], isSelected)}
          </TableCell>
          <TableCell className={cn("text-[11px] text-left p-1", isSelected ? 'text-black' : 'text-primary-light')}>
            {formatCandidateCell(topCandidates[2], isSelected)}
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <div className="rounded-md border border-primary px-[8px] py-px my-[2px]">
      <h3 className="font-medium text-center p-0.5 text-sm text-white py-0 my-[13px]">Roles Needed</h3>
      <div className="max-h-[250px] overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-primary/30 hover:bg-primary/30 sticky top-0">
              <TableHead className="text-[11px] w-[3%] text-primary-light text-left font-semibold p-1">SN</TableHead>
              <TableHead className="text-[11px] text-primary-light font-semibold text-left p-1">Role Title</TableHead>
              <TableHead className="text-[11px] text-primary-light font-semibold text-left p-1">Job Description</TableHead>
              <TableHead className="text-[11px] text-primary-light font-semibold text-left p-1">Candidate 1</TableHead>
              <TableHead className="text-[11px] text-primary-light font-semibold text-left p-1">Candidate 2</TableHead>
              <TableHead className="text-[11px] text-primary-light font-semibold text-left p-1">Candidate 3</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {renderTableContent()}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
